import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="Terms & Conditions of Hire" url="/terms-and-conditions"
        description="Millie's Motorhome Hire terms and conditions - driver requirements, deposits, insurance excess, fuel, mileage, pets and cancellation policy." />
      {/* Header */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms & Conditions</h1>
          <p className="text-xl text-slate-300">Millie's Motorhome Hire</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 prose prose-slate max-w-none">
            
            {/* Definitions */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">Definitions</h2>
            <p className="text-slate-700 mb-4">
              'I','me' and 'my' refers jointly and severally to the person or persons who are the customers. 'This Agreement' means the Rental Agreement, the Insurance Motor Rental Agreement and these Terms and Conditions. In the event of any discrepancy between these Terms and Conditions and any other Millie's Motorhome Hire literature, the provisions of these Terms and Conditions apply.
            </p>
            <p className="text-slate-700 mb-4">
              <strong>'Millie's Motorhome Hire'</strong> means Millie's Motorhome Hire, Clay Hill Farm, Clay Hill Lane, Wattisham, Ipswich, Suffolk, IP7 7JS.
            </p>
            <p className="text-slate-700 mb-4">
              <strong>'Customer'</strong> means the person or persons nominated as the hirer under the heading 'Hirers Name / Contact' on the Rental Agreement, any person whose credit or debit card is presented in payment of the customer's charges, or any person who deems themselves to be the legal entity and ultimately responsible for the motorhome. Should the legal entity be different from the driver, then full name, address and contact phone numbers must be supplied.
            </p>
            <p className="text-slate-700 mb-4">
              <strong>'Rental Period'</strong> means the hire period as stated on the Rental Agreement or any agreed variation thereof and any additional period during which the vehicle is in the customer's possession or control.
            </p>
            <p className="text-slate-700 mb-4">
              <strong>'Vehicle'</strong> means the vehicle as stated on the Rental Agreement and includes tyres, tools, accessories, the living equipment and any other special equipment, documents related to the Vehicle and any replacement or substitute Vehicle which may be provided at the discretion of Millie's Motorhome Hire.
            </p>
            <p className="text-slate-700 mb-4">
              <strong>'Living Equipment'</strong> includes but is not limited to, TV, DVD player and aerial, Microwave, Oven, Radio/CD player, crockery, cutlery, cooking utensils.
            </p>
            <p className="text-slate-700 mb-4">
              <strong>'Security Deposit'</strong> means the amount held as security by Millie's Motorhome Hire in relation to this hire.
            </p>
            <p className="text-slate-700 mb-6">
              <strong>'Overhead damage'</strong> is deemed as any damage sustained to any part of the vehicle, its equipment and or any third party property which occurs above 6 feet (1.83 metres) in height measured from the ground upwards.
            </p>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">1. Depot</h2>
            <p className="text-slate-700 mb-6">
              Millie's Motorhome Hire, Clay Hill Farm, Clay Hill Lane, Wattisham, Ipswich, Suffolk, IP7 7JS
            </p>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">2. Depot Hours</h2>
            <ul className="list-disc ml-6 text-slate-700 mb-2">
              <li>Monday to Friday: 08h00 - 17h00*</li>
              <li>Saturday: 08h00 – 12h00*</li>
              <li>Sundays & Public holidays: closed, unless by prior arrangement</li>
            </ul>
            <p className="text-slate-600 text-sm mb-6">*Last collection/return time for all vehicles is 1 hour prior to depot closing</p>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">3. Hire Rates Quoted Include</h2>
            <p className="text-slate-700 mb-4">
              VAT (20%) - Unlimited miles - UK standard vehicle insurance* - UK Standard equipment & breakdown**
            </p>
            <p className="text-slate-700 mb-4">
              <strong>* Insurance loading:</strong> In some cases, Millie's Motorhome Hire will incur additional charges if the driver to be insured has a traffic conviction, more than 1 insurance claim or is in an occupation which is deemed high risk. These charges will be passed onto the customers.
            </p>
            <p className="text-slate-700 mb-6">
              <strong>** Breakdown:</strong> Millie's Motorhome Hire will supply full breakdown cover to cover any mechanical faults to the base chassis of the motorhome. Any call out charges necessitated by the client through operator error, e.g. a flat vehicle battery, wrong or insufficient fuel, keys locked in Motorhome etc will be the responsibility of the client.
            </p>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">4. Minimum Hire</h2>
            <p className="text-slate-700 mb-6">
              This ranges from 2 to 7 days depending on the season. Millie's Motorhome Hire reserves the right to increase the minimum hire period for certain events.
            </p>

            {/* Section 5 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">5. Payment</h2>
            <p className="text-slate-700 mb-4">
              Availability is on a request and confirm basis at the time of reservation. A reservation is only binding after it has been confirmed by Millie's Motorhome Hire and a deposit payment of £200.00 per hire has been received. Our terms and conditions including cancellation policy apply from this point onwards.
            </p>
            <p className="text-slate-700 mb-4">
              Once the reservation is confirmed, a confirmation email will be sent out to the client. Settlement of the remaining balance will be due eight weeks prior to departure. Millie's Motorhome Hire reserves the right to cancel the booking if payment is not received six weeks prior to departure.
            </p>
            <p className="text-slate-700 mb-4">
              Payment is by credit card (Visa or MasterCard only), debit cards, bank transfers, cash, Stripe, Paypal or cheque in Pounds Sterling.
            </p>
            <p className="text-slate-700 mb-6">
              For late bookings (less than eight weeks before departure) the full rental price is payable on booking. Vehicles will not be released without full payment being completed. Cheques will not be acceptable for late bookings (less than 2 weeks prior to departure).
            </p>

            {/* Section 6 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">6. Cancellation Charges</h2>
            <ul className="list-disc ml-6 text-slate-700 mb-4">
              <li>More than 6 weeks before: Booking Deposit</li>
              <li>6 weeks - 2 weeks: 50% of total hire charge</li>
              <li>2 weeks - No Show: 100% of total hire charge</li>
            </ul>
            <p className="text-slate-700 mb-6">
              Millie's Motorhome Hire cannot postpone or transfer money from one hire to another. Millie's Motorhome Hire Ltd reserves the right to amend our cancellation policy for certain events.
            </p>

            {/* Section 7 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">7. Extras</h2>
            <ul className="list-disc ml-6 text-slate-700 mb-6">
              <li>Parking – No charge</li>
              <li>Bedding Single set (quilt/pillow/towel) £20.00 (per set, per rental)</li>
              <li>Bedding Double set (quilt/2 x pillow/2 x towels) £40.00 (per set, per rental)</li>
              <li>Camp table/chairs - No charge</li>
              <li>Generator: £10.00 per day</li>
              <li>Bike rack (holds up to 4 bikes) £5.00 per day</li>
              <li>Additional driver: £5.00 per person per day, clean driving licence (max 2)</li>
            </ul>

            {/* Section 8 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">8. European Travel</h2>
            <p className="text-slate-700 mb-4">
              It is now a requirement for European travel to obtain a Green Card which must be purchased in advance of travel along with the International driving Licence for each country you will be travelling through. It is the hirers responsibility to obtain the correct documentation for travel.
            </p>
            <p className="text-slate-700 mb-4">
              For those travelling within Europe there is an additional charge of £5.00 per day for European cover (including European roadside assistance). European cover is mandatory and you will be in breach of these terms and conditions should you take a motorhome into Europe without taking out the additional European cover.
            </p>
            <p className="text-slate-700 mb-4">
              Minimum charge term for European cover is 3 days, maximum terms is 21 days.
            </p>
            <p className="text-slate-700 mb-6">
              All current member Countries of the EU are covered by the European Travel Insurance plus the following countries: Gibraltar, Iceland, Liechtenstein, Monaco, Norway, San Marino, Serbia and Switzerland. Travel outside of these countries listed above will be in breach of these terms and conditions and you will not be covered by any insurance.
            </p>

            {/* Section 9 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">9. Ireland Travel</h2>
            <p className="text-slate-700 mb-6">
              For those travelling within Ireland there is an additional charge of £5.00 per day for your Ireland cover (including Irish roadside assistance). Ireland cover is mandatory and you will be in breach of these terms and conditions should you take a motorhome into Ireland without taking out Ireland cover insurance. Minimum charge term for Ireland cover is 3 days, maximum terms is 21 days.
            </p>

            {/* Section 10 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">10. Motorhome Collection & Return</h2>
            <p className="text-slate-700 mb-4">
              <strong>Monday to Friday:</strong> Drop off - 11:00, Collection - 15:00<br />
              Other times may be arranged by appointment if available and at the discretion of Millie's Motorhome Hire
            </p>
            <p className="text-slate-700 mb-4">
              <strong>Saturdays:</strong> Drop off - 11:00, Collection - 15:00<br />
              Other times may be arranged by appointment if available and at the discretion of Millie's Motorhome Hire
            </p>
            <p className="text-slate-700 mb-4">
              <strong>Collection:</strong> Please allow 1 hour for the hand-over at the depot - to complete the documentation and demonstrate your vehicle to you. All drivers need to be present on collection of the motorhome and bring their full valid licence and passport and credit or debit card for security deposit.
            </p>
            <p className="text-slate-700 mb-4">
              <strong>Return:</strong> The vehicle must be returned on the correct date, and before the time stated on the rental agreement - please allow 1 hour for the hand-over at the depot. Vehicles must be returned undamaged, with a full tank of diesel and gas, empty waste water and toilet cassette and the interior clean and in the same condition as they left the depot, otherwise a charge will be made for additional Valeting and or Upholstery cleaning.
            </p>
            <p className="text-slate-700 mb-4">
              <strong>Please note:</strong> there are no toilet emptying facilities at Millie's Motorhome Hire depot so this must be emptied prior to return.
            </p>
            <p className="text-slate-700 mb-6">
              <strong>Late return:</strong> If you will be late returning, Millie's Motorhome Hire must be advised immediately. Failure to advise may result in prosecution for driving whilst uninsured. An additional charge of £100.00 per hour will be payable for all late returns.
            </p>

            {/* Section 11 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">11. Motorhome Systems</h2>
            <p className="text-slate-700 mb-4">
              Millie's Motorhome Hire will carry out a full Pre Checkout Inspection (PCI) on every motorhome before the commencement of hire. The PCI involves testing every system on board the motorhome to ensure they are operating correctly. In conjunction with this the hirer will be taken on a tour of the motorhome and shown how each system works.
            </p>
            <p className="text-slate-700 mb-6">
              Should an onboard system fail during your hire, Millie's Motorhome Hire will make every effort to remedy the fault whilst you are away but in some circumstances this may not be possible and Millie's Motorhome Hire cannot be held responsible and will not refund any monies should a loss of service be encountered nor have any obligation to provide a replacement vehicle. In the case of winter hire, Millie's Motorhome Hire will not be and cannot be held responsible in the event of any damage or inconveniences caused by freezing conditions.
            </p>

            {/* Section 12 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">12. Vehicle Acceptance</h2>
            <p className="text-slate-700 mb-4">
              a) I acknowledge having received the Vehicle in a clean condition and in sound working order in accordance with the Motorhome Handover Checklist and with a full Diesel tank, full LPG gas bottle and full water tank.
            </p>
            <p className="text-slate-700 mb-6">
              b) I acknowledge that Millie's Motorhome Hire will not refund to me any monies if the Vehicle is returned or I cease to have use of the Vehicle or an item of equipment on the vehicle prior to the return date for any reason e.g. Accident, weather, theft or damage.
            </p>

            {/* Section 13 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">13. Change of Vehicle</h2>
            <p className="text-slate-700 mb-6">
              Millie's Motorhome Hire reserves the right, at its absolute discretion, to substitute a comparable or superior Vehicle for the Vehicle ordered. In that event, I shall not be liable for any increased rental or other charges save for any addition running costs pertaining to the substitute vehicle. Such substitution shall not entitle me to any refund and does not constitute a breach of this Agreement.
            </p>

            {/* Section 14 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">14. Rental and Other Charges</h2>
            <p className="text-slate-700 mb-2">I will pay Millie's Motorhome Hire:</p>
            <ul className="list-disc ml-6 text-slate-700 mb-4">
              <li>All rental charges</li>
              <li>The security deposit</li>
              <li>Administration fee of £25.00 per amendment of a confirmed booking or per penalty charge notice received</li>
              <li>The nominated valeting fee and/or upholstery cleaning fee if the Vehicle is not returned with the interior in a clean condition</li>
              <li>The nominated additional fee of £75.00 if the toilet and waste water tank are not emptied prior to the return of the Vehicle</li>
              <li>The cost of refilling either the Diesel and or the LPG tanks should either tank not be returned completely full</li>
            </ul>
            <p className="text-slate-700 mb-2"><strong>Diesel charges:</strong></p>
            <ul className="list-disc ml-6 text-slate-700 mb-4">
              <li>¾ to full = £75.00</li>
              <li>½ to ¾ full = £90.00</li>
              <li>¼ to ½ full = £120.00</li>
              <li>Empty to ¼ full = £140.00</li>
            </ul>
            <p className="text-slate-700 mb-4"><strong>LPG charges:</strong> £40.00 regardless of level</p>
            <ul className="list-disc ml-6 text-slate-700 mb-6">
              <li>Late fee of £100.00 per hour should the vehicle be returned after the agreed time</li>
              <li>The nominated cancellation fee in the event of cancellation</li>
              <li>All government fees and duties</li>
              <li>All parking fines, other fines or penalties and associated administration costs</li>
              <li>Demurrage: the daily rental rate for the period the Vehicle is off fleet (e.g. for accident repairs)</li>
              <li>Any additional costs over and above the security deposit value should damages exceed the security deposit value</li>
              <li>The cost to recover a Vehicle</li>
            </ul>

            {/* Section 16 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">16. Security Deposit</h2>
            <p className="text-slate-700 mb-4">
              There is a refundable GB£1000.00 Security Deposit payable by credit card or debit card. The card should be in the lead drivers name and the amount will be debited from your account immediately. Pre-paid currency cards cannot be accepted.
            </p>
            <p className="text-slate-700 mb-4">
              Millie's Motorhome Hire reserves the right to increase the security deposit in certain circumstances or for specific events e.g. World Cup events, Festivals, clients travelling with pets, for any other hire where the Motorhome is carrying 4 or more adults and if the security deposit is paid in cash to £1500.00. Cheque payments are not accepted against the security deposit.
            </p>
            <ul className="list-disc ml-6 text-slate-700 mb-6">
              <li>On taking delivery of the Vehicle, I agree to pay the Security Deposit</li>
              <li>I irrevocably authorise Millie's Motorhome Hire to deduct from the Security Deposit any amounts due by me</li>
              <li>The security deposit will be refunded within 7 working days of the vehicle being returned to the depot and there are no outstanding insurance claims</li>
              <li>Should any damages exceed the security deposit the customer will be responsible to settle all additional costs within 7 days of your hire end date</li>
            </ul>

            {/* Section 17 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">17. Use of the Vehicle</h2>
            <p className="text-slate-700 mb-2">I agree that during the rental period, I will not and will not allow the Vehicle to be:</p>
            <ul className="list-disc ml-6 text-slate-700 mb-6">
              <li>Driven otherwise than in a cautious, prudent and normal manner</li>
              <li>Used in a manner which could cause damage</li>
              <li>Driven in a prohibited area</li>
              <li>Driven by a person under the influence of alcohol or drugs</li>
              <li>Left with the ignition key in the vehicle while it is unoccupied</li>
              <li>Left unoccupied without the steering wheel security lock applied</li>
              <li>Driven by persons under the age of 23 years or who is not authorised by law</li>
              <li>Damaged by submersion in water or contact with Salt Water</li>
              <li>Used for any illegal purpose or any race, rally or contest</li>
              <li>Used to tow any vehicle or trailer</li>
              <li>Used to carry passengers or property for hire or reward</li>
              <li>Used to carry volatile liquids, gases, explosives or other inflammable material</li>
            </ul>

            {/* Section 18 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">18. Road Restrictions</h2>
            <ul className="list-disc ml-6 text-slate-700 mb-6">
              <li>All Vehicles may only be driven on sealed / bitumen roads</li>
              <li>Millie's Motorhome Hire reserves the right at any time to restrict vehicle movements in certain areas due to adverse road or weather conditions</li>
            </ul>

            {/* Section 21 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">21. Smoking</h2>
            <p className="text-slate-700 mb-6">
              All Millie's Motorhome Hire's motorhomes are non-smoking vehicles. Millie's Motorhome Hire reserves the right to impose additional Valeting and or Upholstery cleaning fees and charge demurrage should smoke be detected in the motorhome. An immediate charge of £250 will be taken if cigarette or cigar smoke is smelt in the motorhome upon its return.
            </p>

            {/* Section 22 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">22. Passengers</h2>
            <p className="text-slate-700 mb-6">
              Millie's Motorhome Hire authorises the use of these vehicles up to the stated number of passengers. Each person is to use the safety restraints provided at all times whilst the vehicle is in motion. Child seats are available (fees applicable) and it is the responsibility of the hirer to fit them themselves. Children up to 12 years of age or 135cm in height, whichever they reach first, must use the correct child restraints appropriate for their weight under UK law.
            </p>

            {/* Section 23 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">23. Pets</h2>
            <p className="text-slate-700 mb-6">
              Millie's Motorhome Hire allows customers to bring well behaved pets, such as dogs or cats. A £20 cleaning charge for every pet taken during the duration of the hire, up to a maximum of two. Otherwise agreed with Millie's Motorhome Hire with permission formally documented via email or means of being electronically recorded.
            </p>

            {/* Section 24 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">24. Drivers</h2>
            <p className="text-slate-700 mb-2"><strong>Age limits:</strong></p>
            <ul className="list-disc ml-6 text-slate-700 mb-4">
              <li>Standard insurance: 23 to 75 years</li>
              <li>Insurance for over 75's drivers: 76-79 years (by special request and agreement only)</li>
              <li>All drivers must have held a valid licence for more than 2 years that entitles them to drive a UK category B vehicle up to 3500kg GMW</li>
              <li>Driver must be able to demonstrate regular driving experience (more than 3 times per week) for at least 2 years</li>
              <li>Drivers holding only an automatic licence can only hire auto models</li>
            </ul>
            <p className="text-slate-700 mb-2"><strong>Documentation required:</strong></p>
            <ul className="list-disc ml-6 text-slate-700 mb-4">
              <li>Full, valid standard driver's licence from all drivers</li>
              <li>Photocard GB licence and NI number for DVLA online check</li>
              <li>Two forms of proof of address matching the address on driving licence</li>
            </ul>
            <p className="text-slate-700 mb-2"><strong>Acceptable licences:</strong></p>
            <p className="text-slate-700 mb-4">
              EU, American, Australian, South African, Canadian, New Zealand and Hong Kong (subject to previously holding a UK licence) are all covered. Other countries need to be referred in advance.
            </p>
            <p className="text-slate-700 mb-2"><strong>Endorsements:</strong></p>
            <p className="text-slate-700 mb-6">
              You must declare any endorsements at time of booking. Up to 6 speeding points within the last 3 years may be accepted (charges apply). More than 6 points or certain conviction codes may not be covered.
            </p>

            {/* Section 25 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">25. Insurance</h2>
            <p className="text-slate-700 mb-4">
              The Vehicle is insured for damage to the Vehicle and the property of a third party; but does not include any personal insurance for the customer (including death or bodily injury to the driver) or cover any personal possessions. The vehicle insurance is arranged by Alan Boswell.
            </p>
            <p className="text-slate-700 mb-4">
              The hire rate quoted includes a standard excess of GB£1000.00 per accident or reported incident. In the event of any damage to either the vehicle or third party property, the hirer will be liable for the first GB£1000.00 per claim.
            </p>
            <p className="text-slate-700 mb-6">
              <strong>Please note:</strong> No insurance cover is offered for overhead damage, any damage deemed as gross negligence (including reversing damage), or any internal damages. Windscreens and/or tyre damage is not covered by the standard vehicle insurance.
            </p>

            {/* Section 27 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">27. Responsibility When Accident Occurs</h2>
            <p className="text-slate-700 mb-2">In the event of any accident, loss or damage arising out of the use of the vehicle, I will:</p>
            <ul className="list-disc ml-6 text-slate-700 mb-6">
              <li>Notify Millie's Motorhome Hire within 24hrs of the happening of the event</li>
              <li>Obtain the names and addresses of third parties and any witnesses and report the event to the nearest police station</li>
              <li>Complete an accident claim form as supplied</li>
              <li>If the accident occurs in Europe, complete the European accident report form</li>
              <li>Not make any admission of liability to other parties</li>
              <li>Assist Millie's Motorhome Hire in handling any claim</li>
            </ul>

            {/* Section 29 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">29. Maintenance</h2>
            <ul className="list-disc ml-6 text-slate-700 mb-4">
              <li>I shall take all reasonable steps to properly maintain the Vehicle, including checking oil and coolant levels, tyre pressures and batteries</li>
              <li>Millie's Motorhome Hire will reimburse up to UK £40.00 reasonably incurred in rectifying mechanical failures (with prior consent and receipts)</li>
              <li>I will pay for the cost of repairing or replacing tyres damaged during the Rental Period</li>
              <li>I will be liable for any costs associated with the incorrect use of fuel</li>
            </ul>
            <p className="text-slate-700 mb-6 font-semibold">
              All Millie's Motorhome Hire motorhomes run on DIESEL.
            </p>

            {/* Section 32 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">32. Terminating the Agreement</h2>
            <p className="text-slate-700 mb-2">Millie's Motorhome Hire may terminate this Agreement and repossess the Vehicle if:</p>
            <ul className="list-disc ml-6 text-slate-700 mb-6">
              <li>I am in breach of any term of this Agreement</li>
              <li>I have obtained the Vehicle through fraud or misrepresentation</li>
              <li>Any statement made by the customer is incorrect</li>
              <li>The Vehicle appears to be abandoned</li>
              <li>The Vehicle is not returned on the agreed return date</li>
              <li>Millie's Motorhome Hire considers the safety of passengers or condition of the Vehicle is in danger</li>
            </ul>

            {/* Section 35 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">35. Force Majeure</h2>
            <p className="text-slate-700 mb-6">
              We will make every effort to ensure that the reserved Motorhome is available for you at the correct time. If, due to circumstances beyond our control, this is not possible and if an alternative or acceptable Motorhome is not available our liability is limited to the refunds of all monies paid by you.
            </p>

            {/* Section 37 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">37. Proper Law</h2>
            <p className="text-slate-700 mb-6">
              This agreement shall be governed by the law of Great Britain in which this agreement was signed.
            </p>

            {/* Section 38 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">38. Data Protection Act</h2>
            <p className="text-slate-700 mb-6">
              The information that you have provided to Millie's Motorhome Hire will be used to fulfill this contract placed with us. We will not pass this information onto any other organisation outside this company other than to our insurer.
            </p>

            {/* Section 39 */}
            <h2 className="text-2xl font-bold text-slate-900 border-b pb-2 mb-4">39. Entire Agreement</h2>
            <p className="text-slate-700 mb-6">
              This Agreement constitutes the entire agreement of the parties and there are no other oral undertakings, warranties or agreements between the parties relating to the subject matter of this agreement. Millie's Motorhome Hire reserves the right to add or amend the vehicle specifications and rental terms.
            </p>

            {/* Last updated */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-slate-500 text-sm">
                Last updated: January 2026
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;
